import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { Modal } from '@atlaskit/onboarding';
import { useThemeObserver } from '@atlaskit/tokens';

import OnboardingCoverImageLight from '../assets/AutoConversionOnboardingCoverLight.svg';
import OnboardingCoverImageDark from '../assets/AutoConversionOnboardingCoverDark.svg';

type EditorConversionModalComponentProps = {
	isOpen: boolean;
	onClose: () => void;
};

export const i18n = defineMessages({
	onboardingModalTitle: {
		id: 'editor-conversion.onboarding.modal.title',
		defaultMessage: 'Convert pages automatically',
		description: 'Title for the editor conversion panel',
	},
	onboardingModalBody: {
		id: 'editor-conversion.onboarding.modal.body',
		defaultMessage:
			'Eligible pages in this space have been automatically converted from the legacy editor by your admin. {learnMoreLink}',
		description: 'Body for the editor conversion panel',
	},
	learnMore: {
		id: 'editor-conversion.onboarding.learn.more',
		defaultMessage: 'Learn more',
		description: 'Learn more about editor conversion',
	},
	buttonText: {
		id: 'editor-conversion.onboarding.button',
		defaultMessage: 'Got it',
		description: 'Editor onboarding button text',
	},
});

export const EditorConversionModalComponent: React.FC<EditorConversionModalComponentProps> = ({
	isOpen,
	onClose,
}) => {
	const intl = useIntl();
	const { colorMode } = useThemeObserver();

	if (!isOpen) return null;

	// #TODO Replace path to image string with @atlaskit/image Image component when @atlaskit/onboarding Modal's image propert will support it
	// see: https://atlassian.design/components/onboarding/benefits-modal/props#BenefitsModal-image
	const onBoardingCoverImagePath =
		colorMode === 'dark' ? OnboardingCoverImageDark : OnboardingCoverImageLight;

	return (
		<Modal
			heading={intl.formatMessage(i18n.onboardingModalTitle)}
			actions={[
				{
					onClick: () => onClose(),
					text: intl.formatMessage(i18n.buttonText),
				},
			]}
			image={onBoardingCoverImagePath}
		>
			<FormattedMessage
				{...i18n.onboardingModalBody}
				values={{
					learnMoreLink: (
						<a
							href="https://support.atlassian.com/confluence-cloud/docs/convert-pages-to-the-new-editor/"
							target="_blank"
						>
							<FormattedMessage {...i18n.learnMore} />
						</a>
					),
				}}
			/>
		</Modal>
	);
};
